// src/screens/GST/ManualMatch/ManualMatchPanel.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Alert,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import GstinExpandableGrid from "./GstinExpandableGrid";

/* ---------------- Utilities ---------------- */

const toNum = (v) => (v == null ? 0 : Number(v) || 0);

function collectSelectedRowsByCache(cacheByGstin, selectedIds, idGetter) {
  const out = [];
  for (const gstin of Object.keys(cacheByGstin || {})) {
    for (const r of cacheByGstin[gstin] || []) {
      if (selectedIds.includes(idGetter(r))) out.push(r);
    }
  }
  return out;
}

/* ===================== MAIN ===================== */

export default function ManualMatchPanel({ token, emp_id, processMonth, onSaved }) {
  /* ===== Phase-1 summaries ===== */
  const [erpSummary, setErpSummary] = useState([]);
  const [g2bSummary, setG2bSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  /* ===== Phase-2 invoice cache ===== */
  const [erpInvoicesByGstin, setErpInvoicesByGstin] = useState({});
  const [g2bInvoicesByGstin, setG2bInvoicesByGstin] = useState({});

  /* ===== Expand (SINGLE GSTIN) ===== */
  const [expandedGstin, setExpandedGstin] = useState("");
  const [loadingGstin, setLoadingGstin] = useState(false);

  /* ===== Hard lock + selection ===== */
  const [activeGstin, setActiveGstin] = useState("");
  const [selectedErpIds, setSelectedErpIds] = useState([]);
  const [selected2bIds, setSelected2bIds] = useState([]);

  /* ===== NEW: Both sides filter ===== */
  const [onlyBothSides, setOnlyBothSides] = useState(true);

  /* ===== Audit ===== */
  const [reasonCode, setReasonCode] = useState("");
  const [notes, setNotes] = useState("");
  const [toleranceAbs, setToleranceAbs] = useState(10);
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  /* ---------------- Load summaries ---------------- */

  const loadSummaries = async () => {
    setLoadingSummary(true);
    setMsg({ type: "", text: "" });

    try {
      const payload = {
        include_matched: "N",
        having_bothsides: onlyBothSides ? "Y" : "N",
      };

      const [erpRes, g2bRes] = await Promise.all([
        axios.post("/api/gst2b/getErpGstinSummary", payload, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.post("/api/gst2b/getG2bGstinSummary", payload, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setErpSummary(erpRes.data || []);
      setG2bSummary(g2bRes.data || []);
    } catch (e) {
      console.error(e);
      setMsg({ type: "danger", text: "Failed to load GSTIN summaries." });
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    loadSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Expand GSTIN ---------------- */

  const toggleExpandGstin = async (gstin) => {
    const g = (gstin || "").trim();
    if (!g) return;

    if (expandedGstin === g) {
      setExpandedGstin("");
      return;
    }

    setExpandedGstin(g);

    const erpLoaded = !!erpInvoicesByGstin[g];
    const g2bLoaded = !!g2bInvoicesByGstin[g];
    if (erpLoaded && g2bLoaded) return;

    setLoadingGstin(true);
    try {
      const [erpRes, g2bRes] = await Promise.all([
        erpLoaded
          ? Promise.resolve({ data: erpInvoicesByGstin[g] })
          : axios.post(
              "/api/gst2b/getErpInvoices",
              { gstin: g, include_matched: "N" },
              { headers: { Authorization: `Bearer ${token}` } }
            ),
        g2bLoaded
          ? Promise.resolve({ data: g2bInvoicesByGstin[g] })
          : axios.post(
              "/api/gst2b/getG2bInvoices",
              { gstin: g, include_matched: "N" },
              { headers: { Authorization: `Bearer ${token}` } }
            ),
      ]);

      if (!erpLoaded)
        setErpInvoicesByGstin((p) => ({ ...p, [g]: erpRes.data || [] }));
      if (!g2bLoaded)
        setG2bInvoicesByGstin((p) => ({ ...p, [g]: g2bRes.data || [] }));
    } catch (e) {
      console.error(e);
      setMsg({
        type: "danger",
        text: `Failed to load invoices for GSTIN ${g}`,
      });
    } finally {
      setLoadingGstin(false);
    }
  };

  /* ---------------- GSTIN Hard Lock ---------------- */

  const onAttemptSetActiveGstin = (gstinFromRow) => {
    const g = (gstinFromRow || "").trim();
    if (!g) return false;

    if (!activeGstin) {
      setActiveGstin(g);
      setMsg({ type: "", text: "" });
      return true;
    }

    if (activeGstin !== g) {
      setMsg({
        type: "danger",
        text: `GSTIN locked to ${activeGstin}. Clear selection to switch.`,
      });
      return false;
    }
    return true;
  };

  const clearSelection = () => {
    setSelectedErpIds([]);
    setSelected2bIds([]);
    setActiveGstin("");
    setExpandedGstin("");
    setReasonCode("");
    setNotes("");
    setMsg({ type: "", text: "" });
  };

  /* ---------------- Configs ---------------- */

  const ERP_CONFIG = {
    getId: (r) => r.ID,
    getVendor: (r) => r.VENDOR_NAME,
    getInvNo: (r) => r.PARTY_BILL_NO,
    getInvDate: (r) => r.PARTY_BILL_DATE,
    getMonth: (r) => r.PROCESS_MONTH,
    getTaxable: (r) => r.TAXABLE_VALUE,
    getTax: (r) => r.TOTAL_TAX,
    getLocked: (r) => (r.IS_LOCKED || "N") === "Y",
  };

  const G2B_CONFIG = {
    getId: (r) => r.ID,
    getVendor: (r) => r.SUPPLIER_NAME,
    getInvNo: (r) => r.BILL_NO,
    getInvDate: (r) => r.BILL_DATE,
    getMonth: (r) => r.PROCESS_MONTH,
    getTaxable: (r) => r.TAXABLE_VALUE,
    getTax: (r) => r.TOTAL_TAX,
    getLocked: (r) => (r.IS_LOCKED || "N") === "Y",
  };

  /* ---------------- Basket totals ---------------- */

  const selectedErpRows = useMemo(
    () =>
      collectSelectedRowsByCache(
        erpInvoicesByGstin,
        selectedErpIds,
        ERP_CONFIG.getId
      ),
    [erpInvoicesByGstin, selectedErpIds]
  );

  const selected2bRows = useMemo(
    () =>
      collectSelectedRowsByCache(
        g2bInvoicesByGstin,
        selected2bIds,
        G2B_CONFIG.getId
      ),
    [g2bInvoicesByGstin, selected2bIds]
  );

  const erpTaxable = selectedErpRows.reduce(
    (a, r) => a + toNum(ERP_CONFIG.getTaxable(r)),
    0
  );
  const erpTax = selectedErpRows.reduce(
    (a, r) => a + toNum(ERP_CONFIG.getTax(r)),
    0
  );
  const g2bTaxable = selected2bRows.reduce(
    (a, r) => a + toNum(G2B_CONFIG.getTaxable(r)),
    0
  );
  const g2bTax = selected2bRows.reduce(
    (a, r) => a + toNum(G2B_CONFIG.getTax(r)),
    0
  );

  const taxableDiff = erpTaxable - g2bTaxable;
  const taxDiff = erpTax - g2bTax;

  const hardOk =
    !!activeGstin &&
    selectedErpIds.length &&
    selected2bIds.length &&
    !!reasonCode &&
    !!notes.trim();

  /* ---------------- Confirm ---------------- */

  const confirmManualMatch = async () => {
    if (!hardOk) {
      setMsg({
        type: "danger",
        text: "Select ERP + 2B invoices and fill Reason + Notes (mandatory).",
      });
      return;
    }

    setPosting(true);
    setMsg({ type: "", text: "" });

    try {
      await axios.post(
        "/api/gst2b/confirmManualMatch",
        {
          gstin: activeGstin,
          erp_ids: selectedErpIds,
          g2b_ids: selected2bIds,
          tolerance_abs: Number(toleranceAbs) || 0,
          reason_code: reasonCode,
          notes,
          emp_id,
          processMonth,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMsg({ type: "success", text: "Manual match saved successfully." });

      clearSelection();
      setErpInvoicesByGstin({});
      setG2bInvoicesByGstin({});
      await loadSummaries();

      if (onSaved) onSaved();
    } catch (e) {
      console.error(e);
      setMsg({ type: "danger", text: "Failed to save manual match." });
    } finally {
      setPosting(false);
    }
  };

  /* ===================== RENDER ===================== */

  return (
    <>
      {/* HEADER */}
      <Card className="mb-2">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-center">
            <Col md={5}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>
                Manual Matching (GSTIN → Vendor → Invoice)
                {loadingSummary && (
                  <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Loading summaries...
                  </span>
                )}
              </div>
            </Col>

            <Col md={4}>
              <Form.Check
                type="checkbox"
                id="mm_onlyBothSides"
                checked={onlyBothSides}
                onChange={(e) => setOnlyBothSides(e.target.checked)}
                label="Show only GSTINs present on both sides (ERP & GSTR-2B)"
                style={{ fontSize: 12 }}
              />
            </Col>

            <Col md={3} className="text-end">
              <Button
                size="sm"
                variant="outline-primary"
                className="me-2"
                onClick={loadSummaries}
              >
                Refresh
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={clearSelection}
              >
                Clear Selection
              </Button>
            </Col>
          </Row>

          {msg.text && (
            <Alert variant={msg.type} className="mt-2 py-1" style={{ fontSize: 12 }}>
              {msg.text}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* MATCH BASKET (TOP) */}
      <Card className="mb-2">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-center">
            <Col md={3}>
              <strong>GSTIN</strong>
              <div>
                <Badge bg={activeGstin ? "dark" : "secondary"}>
                  {activeGstin || "—"}
                </Badge>
              </div>
            </Col>

            <Col md={3} style={{ fontSize: 12 }}>
              <strong>ERP</strong> ({selectedErpIds.length})
              <div>₹{erpTaxable.toFixed(2)} taxable</div>
              <div>₹{erpTax.toFixed(2)} tax</div>
            </Col>

            <Col md={3} style={{ fontSize: 12 }}>
              <strong>2B</strong> ({selected2bIds.length})
              <div>₹{g2bTaxable.toFixed(2)} taxable</div>
              <div>₹{g2bTax.toFixed(2)} tax</div>
            </Col>

            <Col md={3} style={{ fontSize: 12 }}>
              <div>
                Taxable Δ:{" "}
                <Badge
                  bg={Math.abs(taxableDiff) <= toleranceAbs ? "success" : "warning"}
                >
                  ₹{taxableDiff.toFixed(2)}
                </Badge>
              </div>
              <div>
                Tax Δ:{" "}
                <Badge bg={Math.abs(taxDiff) <= toleranceAbs ? "success" : "warning"}>
                  ₹{taxDiff.toFixed(2)}
                </Badge>
              </div>
            </Col>
          </Row>

          <Row className="g-2 mt-2">
            <Col md={2}>
              <InputGroup size="sm">
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={toleranceAbs}
                  onChange={(e) => setToleranceAbs(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
              >
                <option value="">-- Reason --</option>
                <option value="CROSS_MONTH">Cross month</option>
                <option value="ROUNDING">Rounding</option>
                <option value="CREDIT_NOTE">Credit note</option>
                <option value="OTHERS">Others</option>
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Control
                size="sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Audit notes (mandatory)"
              />
            </Col>
            <Col md={2}>
              <Button
                size="sm"
                className="w-100"
                disabled={!hardOk || posting}
                onClick={confirmManualMatch}
              >
                {posting ? "Saving..." : "Confirm Match"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* GRIDS */}
      <Row className="g-2">
        <Col md={6}>
          <GstinExpandableGrid
            title="ONLY ERP (Unmatched)"
            side="ERP"
            summaryRows={erpSummary}
            invoicesByGstin={erpInvoicesByGstin}
            expandedGstin={expandedGstin}
            loadingGstin={loadingGstin}
            onToggleExpand={toggleExpandGstin}
            selectedIds={selectedErpIds}
            setSelectedIds={setSelectedErpIds}
            activeGstin={activeGstin}
            onAttemptSetActiveGstin={onAttemptSetActiveGstin}
            config={ERP_CONFIG}
          />
        </Col>

        <Col md={6}>
          <GstinExpandableGrid
            title="ONLY 2B (Unmatched)"
            side="2B"
            summaryRows={g2bSummary}
            invoicesByGstin={g2bInvoicesByGstin}
            expandedGstin={expandedGstin}
            loadingGstin={loadingGstin}
            onToggleExpand={toggleExpandGstin}
            selectedIds={selected2bIds}
            setSelectedIds={setSelected2bIds}
            activeGstin={activeGstin}
            onAttemptSetActiveGstin={onAttemptSetActiveGstin}
            config={G2B_CONFIG}
          />
        </Col>
      </Row>
    </>
  );
}
